import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Spin,
  message,
  Avatar,
  Descriptions,
  Row,
  Col,
  Table,
  Empty,
  Image,
  Dropdown,
  Space,
  Modal,
  Form,
  Select,
} from 'antd';
import {
  EditOutlined,
  DollarOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  BankOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { expensesApi, bankAccountsApi } from '../../utils/api';
import { formatMoneyFa } from '../../utils/formatPriceToman';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const ViewExpense = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const { canEdit, canDelete } = useResourcePermissions('expenses');
  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isPayModalVisible, setIsPayModalVisible] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payForm] = Form.useForm();

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/expenses', { state: buildGoBackState() });
  };

  useEffect(() => {
    fetchExpense();
    fetchBankAccounts();
  }, [id]);

  const fetchBankAccounts = async () => {
    try {
      const res = await bankAccountsApi.getAll();
      if (res?.data?.accounts) setBankAccounts(res.data.accounts);
      else if (res?.accounts) setBankAccounts(res.accounts);
    } catch (error) {
      console.error('Error fetching bank accounts', error);
    }
  };

  const fetchExpense = async () => {
    setLoading(true);
    try {
      const res = await expensesApi.getById(id);
      if (res?.data?.expense) {
        setExpense(res.data.expense);
        setTransactions(Array.isArray(res.data.expense.transactions) ? res.data.expense.transactions : []);
      }
    } catch (error) {
      console.error('Error fetching expense:', error);
      message.error('خطا در دریافت اطلاعات هزینه');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'warning', text: 'در انتظار', icon: <ClockCircleOutlined /> },
      approved: { color: 'success', text: 'تأیید شده', icon: <CheckCircleOutlined /> },
      rejected: { color: 'error', text: 'رد شده', icon: <CloseCircleOutlined /> },
    };
    const s = statusMap[status] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={s.color} icon={s.icon} style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 12px' }}>
        {s.text}
      </Tag>
    );
  };

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(true);
    try {
      await expensesApi.updateStatus(id, newStatus);
      message.success('وضعیت هزینه با موفقیت تغییر کرد');
      fetchExpense();
    } catch (error) {
      message.error('خطا در تغییر وضعیت هزینه');
    } finally {
      setStatusLoading(false);
    }
  };

  const handlePay = async (values) => {
    setPayLoading(true);
    try {
      await expensesApi.pay(id, { bank_account_id: values.bank_account_id });
      message.success('پرداخت مستقیم هزینه با موفقیت انجام شد');
      setIsPayModalVisible(false);
      fetchExpense();
    } catch (error) {
      message.error('خطا در پرداخت هزینه');
    } finally {
      setPayLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف هزینه</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این هزینه اطمینان دارید؟</span>,
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await expensesApi.delete(id);
          message.success('هزینه با موفقیت حذف شد');
          navigate(returnToRef.current || '/finance/expenses', { state: buildGoBackState() });
        } catch (error) {
          message.error('خطا در حذف هزینه');
        }
      },
    });
  };

  const statusMenuItems = [
    {
      key: 'pending',
      label: <span style={{ fontFamily: 'CustomFont' }}>در انتظار</span>,
      icon: <ClockCircleOutlined />,
      onClick: () => handleStatusChange('pending'),
    },
    {
      key: 'approved',
      label: <span style={{ fontFamily: 'CustomFont' }}>تأیید</span>,
      icon: <CheckCircleOutlined />,
      onClick: () => handleStatusChange('approved'),
    },
    {
      key: 'rejected',
      label: <span style={{ fontFamily: 'CustomFont' }}>رد</span>,
      icon: <CloseCircleOutlined />,
      danger: true,
      onClick: () => handleStatusChange('rejected'),
    },
  ];

  const transactionColumns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>,
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      render: (date) => <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عنوان</span>,
      dataIndex: 'title',
      key: 'title',
      render: (title) => {
        const titles = {
          'expense-added': 'ثبت هزینه',
          'expense-updated': 'بروزرسانی هزینه',
        };
        return <span style={{ fontFamily: 'CustomFont' }}>{titles[title] || title}</span>;
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>,
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <span className={record.type === 'Cr' ? 'text-green-600' : 'text-red-600'} style={{ fontFamily: 'CustomFont' }}>
          {record.type === 'Cr' ? '+' : '-'}{formatMoneyFa(amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>حساب بانکی</span>,
      dataIndex: 'bank_account',
      key: 'bank_account',
      render: (bankAccount) =>
        bankAccount ? (
          <span style={{ fontFamily: 'CustomFont' }}>
            {bankAccount.account_name} {bankAccount.bank_name ? `(${bankAccount.bank_name})` : ''}
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>موجودی</span>,
      dataIndex: 'bank_balance',
      key: 'bank_balance',
      render: (balance) => <span style={{ fontFamily: 'CustomFont' }}>{formatMoneyFa(balance)}</span>,
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!expense) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Empty description={<span style={{ fontFamily: 'CustomFont' }}>هزینه یافت نشد</span>} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <DollarOutlined className="text-red-600 dark:text-red-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  {expense.item_name}
                </h1>
                <div className="mt-1">
                  {getStatusTag(expense.status)}
                </div>
              </div>
            </div>
            <Space wrap>
              {canEdit ? (
                <Dropdown menu={{ items: statusMenuItems }} trigger={['click']}>
                  <Button loading={statusLoading} style={{ fontFamily: 'CustomFont' }}>
                    تغییر وضعیت <DownOutlined />
                  </Button>
                </Dropdown>
              ) : null}
              {expense.status === 'approved' && canEdit ? (
                <Button
                  type="primary"
                  icon={<BankOutlined />}
                  onClick={() => setIsPayModalVisible(true)}
                  style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}
                >
                  پرداخت مستقیم
                </Button>
              ) : null}
              {canEdit ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/finance/expenses/${id}/edit`)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  حذف
                </Button>
              ) : null}
              <Button
                icon={<ArrowRightOutlined />}
                onClick={goBackToList}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={24} lg={16}>
                <Card className="dark:bg-gray-800 border-0 shadow-sm h-full" styles={{ body: { fontFamily: 'CustomFont' } }}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                      <DollarOutlined className="text-red-600 text-2xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold m-0 dark:text-white" style={{ fontFamily: 'CustomFont' }}>
                        {expense.item_name}
                      </h2>
                      <div className="mt-1">{getStatusTag(expense.status)}</div>
                    </div>
                  </div>

                  <Descriptions
                    column={{ xs: 1, sm: 2 }}
                    bordered
                    size="small"
                    labelStyle={{ fontFamily: 'CustomFont', whiteSpace: 'nowrap', minWidth: 110 }}
                    contentStyle={{ fontFamily: 'CustomFont', whiteSpace: 'nowrap' }}
                  >
                    <Descriptions.Item label="مبلغ">
                      <span className="text-blue-600 font-bold text-lg">
                        {formatMoneyFa(expense.price, expense.currency?.currency_symbol)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="تاریخ خرید">
                      {formatDate(expense.purchase_date)}
                    </Descriptions.Item>
                    <Descriptions.Item label="خریداری از">
                      {expense.purchase_from || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="کارمند">
                      {expense.user ? (
                        <Space>
                          <Avatar src={expense.user.image_url} size="small">
                            {expense.user.name?.[0]}
                          </Avatar>
                          {expense.user.name}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="پروژه">
                      {expense.project?.project_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="دسته‌بندی">
                      {expense.category?.category_name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="حساب بانکی">
                      {expense.bank_account_id ? (
                        <Space>
                          <BankOutlined />
                          {transactions?.[0]?.bank_account?.account_name || '-'}
                          {transactions?.[0]?.bank_account?.bank_name
                            ? ` (${transactions[0].bank_account.bank_name})`
                            : ''}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="نرخ تبدیل">
                      {toPersianNumber(expense.exchange_rate)}
                    </Descriptions.Item>
                    {expense.description && (
                      <Descriptions.Item label="توضیحات" span={2}>
                        {expense.description}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  title={<span style={{ fontFamily: 'CustomFont' }}>فاکتور / رسید</span>}
                  className="dark:bg-gray-800 border-0 shadow-sm h-full"
                  styles={{ body: { fontFamily: 'CustomFont', textAlign: 'center' } }}
                >
                  {expense.bill_url && expense.bill_url !== '' ? (
                    expense.bill_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <Image src={expense.bill_url} className="rounded-lg max-w-full" />
                    ) : (
                      <a
                        href={expense.bill_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-lg"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        دانلود / مشاهده فایل
                      </a>
                    )
                  ) : (
                    <Empty description={<span style={{ fontFamily: 'CustomFont' }}>فاکتوری آپلود نشده است</span>} />
                  )}
                </Card>
              </Col>
            </Row>

            {/* جدول تراکنش‌ها */}
            {transactions.length > 0 && (
              <Card
                title={<span style={{ fontFamily: 'CustomFont' }}>تراکنش‌های مرتبط</span>}
                className="dark:bg-gray-800 border-0 shadow-sm"
              >
                <Table
                  columns={transactionColumns}
                  dataSource={transactions}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>پرداخت هزینه از حساب بانکی</span>}
        open={isPayModalVisible}
        onCancel={() => setIsPayModalVisible(false)}
        footer={null}
      >
        <Form form={payForm} layout="vertical" onFinish={handlePay}>
          <Form.Item
            name="bank_account_id"
            label={<span style={{ fontFamily: 'CustomFont' }}>انتخاب حساب بانکی</span>}
            rules={[{ required: true, message: 'لطفاً حساب بانکی را انتخاب کنید' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="یک حساب بانکی انتخاب کنید"
            >
              {bankAccounts.map((account) => (
                <Select.Option key={account.id} value={account.id}>
                  {account.account_name} {account.bank_name ? `(${account.bank_name})` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsPayModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>
                لغو
              </Button>
              <Button type="primary" htmlType="submit" loading={payLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}>
                ثبت پرداخت
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default ViewExpense;
